import { ArrowDown, ArrowUp, Copy, TextAa, Trash } from "@phosphor-icons/react";
import {
  MAX_CAMADAS_POR_CELULA,
  MAX_CAMADAS_POR_FOLHA,
  MAX_FIGURINHAS_DISTINTAS_POR_FOLHA,
  type Camada,
  type DestinoNaOrdem,
} from "@foto/shared";
import { cx } from "../../components/ui.js";
import { nomeDaCamada } from "./camadas-da-celula.js";
import { figurinhaDe, type FigurinhasDasCamadas } from "./figurinhas-das-camadas.js";
import type { TotaisDaFolha } from "./limites-da-folha.js";

/**
 * A lista de camadas, da frente para trás (a de cima da lista é a que fica por cima da foto — a ordem do array
 * é de baixo para cima, e aqui se mostra ao contrário, como numa pilha de papéis vista de cima).
 *
 * Cada linha seleciona a camada (o corpo dela) e tem os botões de subir, descer, duplicar e apagar — o
 * mesmo que o editor faz por gesto e por teclado, ao alcance do dedo. "Subir" é para a frente.
 */
export function PilhaDeCamadas({
  camadas,
  figurinhas,
  selecionada,
  aoSelecionar,
  aoMoverNaOrdem,
  aoDuplicar,
  aoApagar,
  duplicarDesabilitado,
  totaisDaFolha,
}: {
  camadas: readonly Camada[];
  figurinhas: FigurinhasDasCamadas | undefined;
  selecionada: string | null;
  aoSelecionar: (id: string) => void;
  aoMoverNaOrdem: (id: string, para: DestinoNaOrdem) => void;
  aoDuplicar: (id: string) => void;
  aoApagar: (id: string) => void;
  /** Mais uma camada não cabe (a foto, a folha ou as figurinhas diferentes chegaram ao teto). */
  duplicarDesabilitado: boolean;
  /** O que a folha inteira já usa, contra os tetos do servidor. */
  totaisDaFolha: TotaisDaFolha;
}) {
  const cheia = camadas.length >= MAX_CAMADAS_POR_CELULA;
  const daFrenteParaTras = [...camadas].reverse();

  return (
    <section aria-label="Camadas desta foto" className="flex flex-col gap-2">
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="text-[0.9375rem] font-medium text-texto">Camadas</h3>
        <span
          className={cx(
            "text-[0.8125rem] tabular-nums",
            cheia ? "text-descartar" : "text-texto-suave",
          )}
        >
          {camadas.length} de {MAX_CAMADAS_POR_CELULA}
        </span>
      </div>

      {camadas.length === 0 ? (
        <p className="text-[0.8125rem] leading-relaxed text-texto-fraco">
          Nenhuma ainda. Adicione uma figurinha, um logo ou um texto para ele aparecer sobre a foto.
        </p>
      ) : (
        <ul className="flex flex-col gap-1.5">
          {daFrenteParaTras.map((camada, i) => {
            const figurinha =
              camada.tipo === "imagem" ? figurinhaDe(figurinhas, camada.figurinhaId) : undefined;
            const estado =
              camada.tipo === "imagem" ? figurinhas?.get(camada.figurinhaId)?.estado : undefined;
            const nome = nomeDaCamada(camada, figurinha);
            const ativa = camada.id === selecionada;
            const naFrente = i === 0;
            const noFundo = i === daFrenteParaTras.length - 1;

            return (
              <li
                key={camada.id}
                className={cx(
                  "flex items-center rounded-peca border",
                  ativa ? "border-brasa bg-brasa-fraca/40" : "border-borda bg-superficie",
                )}
              >
                <button
                  type="button"
                  aria-pressed={ativa}
                  aria-label={`Selecionar: ${nome}`}
                  onClick={() => aoSelecionar(camada.id)}
                  className="flex min-h-11 min-w-0 flex-1 items-center gap-2 py-1 pl-1.5 text-left"
                >
                  <Miniatura camada={camada} url={figurinha?.urls.mini} />
                  <span className="min-w-0">
                    <span className="block truncate text-[0.875rem] text-texto">{nome}</span>
                    {estado === "indisponivel" && (
                      <span className="block text-[0.75rem] text-descartar">
                        Imagem indisponível
                      </span>
                    )}
                    {estado === "erro" && (
                      <span className="block text-[0.75rem] text-descartar">Não carregou</span>
                    )}
                  </span>
                </button>
                <Acao
                  rotulo={`Trazer para a frente: ${nome}`}
                  desabilitado={naFrente}
                  aoClicar={() => aoMoverNaOrdem(camada.id, "uma-acima")}
                >
                  <ArrowUp size={18} aria-hidden />
                </Acao>
                <Acao
                  rotulo={`Levar para trás: ${nome}`}
                  desabilitado={noFundo}
                  aoClicar={() => aoMoverNaOrdem(camada.id, "uma-abaixo")}
                >
                  <ArrowDown size={18} aria-hidden />
                </Acao>
                <Acao
                  rotulo={`Duplicar: ${nome}`}
                  desabilitado={duplicarDesabilitado}
                  aoClicar={() => aoDuplicar(camada.id)}
                >
                  <Copy size={18} aria-hidden />
                </Acao>
                <Acao rotulo={`Apagar: ${nome}`} aoClicar={() => aoApagar(camada.id)} perigo>
                  <Trash size={18} aria-hidden />
                </Acao>
              </li>
            );
          })}
        </ul>
      )}

      <p className="text-[0.75rem] leading-relaxed text-texto-fraco">
        A folha toda: {totaisDaFolha.camadas} de {MAX_CAMADAS_POR_FOLHA} camadas
        {totaisDaFolha.figurinhas > 0 &&
          `, ${totaisDaFolha.figurinhas} de ${MAX_FIGURINHAS_DISTINTAS_POR_FOLHA} figurinhas diferentes`}
        .
      </p>
    </section>
  );
}

function Acao({
  rotulo,
  aoClicar,
  desabilitado,
  perigo,
  children,
}: {
  rotulo: string;
  aoClicar: () => void;
  desabilitado?: boolean;
  perigo?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={rotulo}
      title={rotulo}
      disabled={desabilitado}
      onClick={aoClicar}
      className={cx(
        "inline-flex size-11 shrink-0 items-center justify-center rounded-peca transition-colors",
        "disabled:cursor-not-allowed disabled:opacity-30",
        perigo
          ? "text-descartar hover:bg-descartar/10"
          : "text-texto-suave hover:bg-superficie-alta hover:text-texto",
      )}
    >
      {children}
    </button>
  );
}

/** O quadradinho da linha: a miniatura da figurinha sobre o xadrez, ou o ícone de texto. */
function Miniatura({ camada, url }: { camada: Camada; url: string | undefined }) {
  if (camada.tipo === "texto") {
    return (
      <span
        aria-hidden
        className="inline-flex size-9 shrink-0 items-center justify-center rounded-peca bg-superficie-alta text-texto-suave"
      >
        <TextAa size={20} />
      </span>
    );
  }
  return (
    <span
      aria-hidden
      className="fundo-xadrez relative inline-block size-9 shrink-0 overflow-hidden rounded-peca"
    >
      {url && (
        <img
          src={url}
          alt=""
          draggable={false}
          className="absolute inset-0 size-full object-contain p-0.5"
        />
      )}
    </span>
  );
}
